import { Button, Link, Stack, Typography } from '@mui/material'
import { format, isBefore } from 'date-fns'
import { find } from 'lodash'
import { FC, MouseEvent, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MeetingDTO } from '../../../../api/gen/base'
import ArrowRightIcon from '../../../../assets/icons/ArrowRight.svg?react'
import PenIcon from '../../../../assets/icons/Pen.svg?react'
import TrashIcon from '../../../../assets/icons/Trash.svg?react'
import { ModalDialogWrapper } from '../../../../components/ModalDialogWrapper'
import { RECORD_ROUTE, RECORDS_ROUTE } from '../../../../constants/routing.constants'
import { MeetDeleteDialog } from '../../../../dialogs/MeetDeleteDialog/MeetDeleteDialog'
import { MeetEditDialog } from '../../../../dialogs/MeetEditDialog/MeetEditDialog'
import { useWrapperStore } from '../../../../store/storeWrapper'

type Props = {
    open: boolean
    onClose: VoidFunction
    data?: MeetingDTO
}

export const MeetsInfoDialog: FC<Props> = ({ open, onClose, data }) => {
    const navigate = useNavigate()

    const { projectListFull } = useWrapperStore((state) => state)

    const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false)
    const [isOpenEditDialog, setIsOpenEditDialog] = useState(false)

    const projectName = useMemo(() => {
        const projectDetail = find(projectListFull, (item) => {
            return item.id === data?.workplace_id
        })

        return projectDetail?.title ?? 'Без проекта'
    }, [projectListFull])

    const handleOpenRecord = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()

        navigate(data?.record_id ? `${RECORD_ROUTE}/${data?.record_id}` : RECORDS_ROUTE)

        onClose?.()
    }

    const handleOpenDeleteDialog = () => {
        setIsOpenDeleteDialog(true)
        onClose?.()
    }
    const handleCloseDeleteDialog = () => setIsOpenDeleteDialog(false)

    const handleOpenEditDialog = () => {
        setIsOpenEditDialog(true)
        onClose?.()
    }
    const handleCloseEditDialog = () => setIsOpenEditDialog(false)

    const canEdit = !!data && isBefore(new Date(), data.start_at)

    return (
        <>
            <MeetDeleteDialog
                id={data?.id}
                open={isOpenDeleteDialog}
                handleClose={handleCloseDeleteDialog}
            />
            <MeetEditDialog
                idMeeting={data?.id}
                open={isOpenEditDialog}
                handleClose={handleCloseEditDialog}
                source={data?.source ?? ''}
            />
            <ModalDialogWrapper
                open={open}
                handleClose={onClose}
                title={
                    <Typography
                        variant="h5"
                        sx={{
                            width: '100%',
                            display: '-webkit-box',
                            WebkitLineClamp: '3',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {data?.title ?? ''}
                    </Typography>
                }
                footerActions={
                    canEdit ? (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<TrashIcon color="#5A5F72" />}
                                sx={{
                                    width: '50%',
                                    color: '#181B25',
                                    textWrapMode: 'nowrap',
                                    p: '6px 12px',
                                    fontWeight: 400,
                                    '.MuiButton-startIcon': {
                                        marginRight: 0,
                                    },
                                }}
                                onClick={handleOpenDeleteDialog}>
                                Удалить встречу
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<PenIcon color="#fff" />}
                                sx={{
                                    width: '50%',
                                    color: 'white',
                                    textWrapMode: 'nowrap',
                                    p: '6px 12px',
                                    fontWeight: 400,
                                    '.MuiButton-startIcon': {
                                        marginRight: 0,
                                    },
                                }}
                                onClick={handleOpenEditDialog}>
                                Редактировать
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            endIcon={<ArrowRightIcon color="#fff" />}
                            onClick={handleOpenRecord}
                            sx={{
                                color: 'white',
                                width: '100%',
                                '.MuiButton-endIcon': {
                                    marginLeft: 0,
                                },
                            }}>
                            К записи встречи
                        </Button>
                    )
                }>
                <Stack direction="column" gap={1}>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Проект:
                        </Typography>
                        <Typography
                            variant="body2"
                            color="#181B25"
                            sx={{
                                width: '100%',
                                display: '-webkit-box',
                                WebkitLineClamp: '1',
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {projectName}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Дата начала:
                        </Typography>
                        <Typography variant="body2" color="#181B25">
                            {data?.start_at ? format(data?.start_at, 'dd.MM.yyyy, HH:mm') : ''}
                        </Typography>
                    </Stack>
                    <Link
                        href={data?.source}
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                        color="#006AD8"
                        sx={{
                            width: '100%',
                            display: '-webkit-box',
                            WebkitLineClamp: '1',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {data?.source}
                    </Link>
                </Stack>
            </ModalDialogWrapper>
        </>
    )
}
